import { useEffect, useMemo, useRef, useState } from "react";

import { Loader } from "@vds/loaders";
import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getAllRoiListWithCvApps, ROI_LIST_WITH_CVAPPS_LOADING_STATUS, setAllBaselineCount, setStatisticsSelection, clearStatisticsSelection } from "../../store/actions";
import { fetchKnowiWidgets, storeWidgetList } from "../../store/actions/knowi-actions";
import { getWidgetLocationName } from "../../global/utility/widgetUtils";
import "./charts.css";
import {
  arrangeAsAlphabeticalOrder,
  arrangeAsAlphabeticalOrderWithField,
  arrangeWidgetNameAsAlphabeticalOrder,
  dataliter,
  extractNameFromFullWidgetName,
  extractNameFromFullWidgetNameForWidgetFilterOFFLoors,
  filterRoiCvappfromSiteSelection,
  getAppName,
  getDateForKNOWIAPis,
  getParentWidgetName,
  getPickerDateFromUTCDate,
  getSelectedTagList,
  getTagListForEncryptAPI,
  setAllTagsBaseLineCount,
  processUnmatchedTagMatches,
  setpathParam,
  isValidRoiWithSiteAndApp,
  isRoiWithDeployedStatus,
  segregateLocationsOnBaisOfType
} from "../../global/utility/statisticsUtil";
import moment from "moment";
import { CVAPP_OBJECTBLUR } from "../../global/constants/cvappConstants";
import { StatisticProvider } from "./contexts/StatisticContext";
import GettingStartedModal from "../Dashboard/GettingStartedModal";
import LandingFilterBar from "./components/LandingFilterBar";
import StatsApp from "./components/StatsApp";
import { complete_widgetList } from "./statisticsConstants";
import { CHART_TYPES } from "./statisticsConstants";

const Statistics = (props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statisticsCharts = useSelector((state) => state.userAppSettingsReducer.StatisticsCharts);
  const appNameFromQuery = searchParams.get("appName") || null;
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
  const [baselineVisibility, setBaselineVisibility] = useState([]);
  const [showHideFilter, setShowHideFilter] = useState(true);
  const widgetsState = useSelector((state) => state.statisticsReducer);
  const deviceListItem = useSelector((state) => state.deviceReducer);
  const widgetListFromWidgetstate = arrangeWidgetNameAsAlphabeticalOrder(widgetsState?.widgets);
  const cvAppNames = useSelector((state) => state.cvAppReducer.cvAppList);

  const rawAppName = getAppName(state, appNameFromQuery, "All Apps");
  const resolvedAppName = cvAppNames?.length
    ? (cvAppNames.find((c) => c?.resources?.displayName?.toLowerCase() === rawAppName?.toLowerCase())?.resources?.displayName ?? rawAppName)
    : rawAppName;

  // Restore a persisted selection snapshot after a hard refresh, but only when it's safe:
  // - `state` (router history state) is fresher/more authoritative when present (e.g. a brand-new
  //   Dashboard-widget navigation) and must win over an old persisted snapshot.
  // - The snapshot must match the app currently named in the URL, otherwise a stale snapshot for a
  //   different app would leak into an unrelated app's selection. Compare against `resolvedAppName`
  //   (the decoded display name, e.g. "Parking Occupancy") rather than `appNameFromQuery` (the raw
  //   URL slug, e.g. "parking_occupancy") — `appName` is always persisted/read as the display name,
  //   so comparing it to the slug would never match and silently discard every snapshot.
  let persistedSelection = null;
  if (!state && appNameFromQuery) {
    const stored = widgetsState?.statisticsSelection;
    if (stored?.appName === resolvedAppName) {
      persistedSelection = stored;
    }
  }

  const [appName, setAppName] = useState(persistedSelection?.appName ?? resolvedAppName);
  const [appNameForViewTag, setAppNameForViewTag] = useState(persistedSelection?.appNameForViewTag ?? resolvedAppName);
  const roiListWithCvapps = useSelector((state) => state.polygonRoiReducer?.roiListWithCvapps);
  const roiListItem = useMemo(
    () => roiListWithCvapps?.filter(isRoiWithDeployedStatus),
    [roiListWithCvapps]
  );
  const roiListWithCvappsLoadingStatus = useSelector((state) => state.polygonRoiReducer.roiListWithCvappsLoadingStatus);
  const fovListItem = useSelector((state) => state.deviceReducer.fovList);
  const [isLoading, setIsLoading] = useState(true);

  const [baselineDataSourcesToggleDropdown, setBaselineDataSourcesToggleDropdown] = useState(false);
  const [appNameToggleDropdown, setAppNameToggleDropdown] = useState(true);
  const [objectNameToggleDropdown, setObjectNameToggleDropdown] = useState(true);
  const [siteNameToggleDropdown, setSiteNameToggleDropdown] = useState(true);

  const siteReducer = useSelector((state) => state.sitesReducer.siteTagList);
  const allSiteIds = useMemo(() => siteReducer?.sites?.map((site) => site.id) || [], [siteReducer?.sites]);
  const roisListWithApps = useMemo(
    () => roiListItem?.filter((roiItem) => isValidRoiWithSiteAndApp(roiItem, allSiteIds)),
    [roiListItem, allSiteIds]
  );

  const cvAppRoisWithCVApps = useMemo(
    () => roiListItem?.filter((roiItem) => isValidRoiWithSiteAndApp(roiItem, allSiteIds, appName)),
    [roiListItem, allSiteIds, appName]
  );

  const [locationsWithRoiCvapps, setLocationsWithRoiCvapps] = useState(dataliter(cvAppRoisWithCVApps));
  useEffect(() => {
    setLocationsWithRoiCvapps(dataliter(roiListItem?.filter((roiItem) => isValidRoiWithSiteAndApp(roiItem, allSiteIds, appName))));
  }, [roiListItem, siteReducer?.sites, appName, allSiteIds]);
  const [sites, setSitesTagList] = useState([]);
  useEffect(() => {
    if (siteReducer?.sites) {
      setSitesTagList(arrangeAsAlphabeticalOrderWithField(siteReducer.sites, "name"));
    } else {
      setSitesTagList([]);
    }
  }, [siteReducer?.sites])

  const cvappNameListFilter = roiListItem?.filter((roiItem) => isValidRoiWithSiteAndApp(roiItem, allSiteIds, null, true)) || [];

  const [filteredCvAppROiList, setFilteredCvAppROiList] = useState(cvappNameListFilter);
  const cvappNameListNames = cvappNameListFilter?.map((m) => {
    return m?.cvapps?.map(j => j?.resources?.displayName);
  }).flat().filter(y => y != CVAPP_OBJECTBLUR);
  const cvappNameList = [...new Set(cvappNameListNames)];

  const TAppList = widgetListFromWidgetstate?.filter((m) => {
    return m?.name && m.name.indexOf("lvl1") !== -1;
  });

  const landingCVAppNameList = arrangeAsAlphabeticalOrder([
    ...new Set(
      cvAppNames?.map((m) => {
        return m?.resources?.displayName;
      })
    )
  ]).filter((f) => {
    return f !== CVAPP_OBJECTBLUR;
  });

  const WidgetListFromCvApp = arrangeWidgetNameAsAlphabeticalOrder(TAppList);
  const [widgetList, setWidgetList] = useState(TAppList);

  const [floors, setFloorsTagList] = useState(
    sites?.length && siteReducer?.floors?.length
      ? siteReducer.floors.filter((l) => {
          if (l?.tagids !== undefined && Array.isArray(l.tagids) && locationsWithRoiCvapps.indexOf(l.id) !== -1 && sites?.length) {
            return l.tagids.indexOf(sites[0].id) !== -1;
          }
          return false;
        })
      : []
  );
  const [areaTagList, setAreasTagList] = useState(
    siteReducer?.areas
      ? siteReducer.areas.filter((m) => {
          return m?.id && locationsWithRoiCvapps.indexOf(m.id) !== -1;
        })
      : []
  );
  const [subAreaTagList, setSubAreasTagList] = useState(
    siteReducer?.subareas
      ? siteReducer.subareas.filter((m) => {
          return m?.id && locationsWithRoiCvapps.indexOf(m.id) !== -1;
        })
      : []
  );

  const [eventAppObjectClassList, setEventAppObjectClassList] = useState([]);
  const [eventAppColorsClassList, setEventAppColorsClassList] = useState([]);
  const [selectUpperClothingColors, setSelectUpperClothingColors] = useState([]);
  const [selectLowerClothingColors, setSelectLowerClothingColors] = useState([]);
  const [activeLocationAccordion, setActiveLocationAccordion] = useState(null);
  const [showObjectTypeDropdown, setShowObjectTypeDropdown] = useState(false);

  const [selectedSite, setSelectedSite] = useState(
    persistedSelection?.selectedSite ?? (state?.site !== undefined ? state?.site : [])
  );
  const [selectedFloors, setSelectedFloors] = useState(() => {
    if (persistedSelection?.selectedFloors) return persistedSelection.selectedFloors;
    if (state?.floor?.length > 0) return state.floor;
    // Dashboard area-level widget: floor is cleared in StatWidget before navigation.
    // Derive the parent floor from the area's tagids so it shows as selected in the filter.
    if (state?.area?.length > 0 && siteReducer?.floors?.length > 0) {
      const parentFloor = siteReducer.floors.find((f) => f.id === state.area[0]?.tagids?.[0]);
      return parentFloor ? [parentFloor] : [];
    }
    return [];
  });
  const [selectedAreas, setSelectedAreas] = useState(
    persistedSelection?.selectedAreas ?? (state?.area !== undefined ? state?.area : [])
  );
  const [selectedSubAreas, setSelectedSubAreas] = useState(
    persistedSelection?.selectedSubAreas ?? (state?.subarea !== undefined ? state?.subarea : [])
  );
  const [selectAccordionData, setSelectedAccordionData] = useState({
    floors: selectedFloors,
    areas: selectedAreas,
    subareas: selectedSubAreas
  });
  const [selectedEventAppObject, setSelectedEventAppObject] = useState("");
  const [activeDatepickerListIndex, setActiveDatepickerListIndex] = useState(2);

  const [siteNameForViewTag, setSiteNameForViewTag] = useState(
    persistedSelection?.siteNameForViewTag ?? (state?.siteName !== undefined ? state?.siteName : sites?.length >= 1 ? sites?.[0].name : "")
  );
  const [selectedSiteName, setSelectedSiteAppName] = useState(
    persistedSelection?.selectedSiteName ?? (state?.siteName !== undefined ? state?.siteName : sites?.length >= 1 ? sites?.[0].id : "All Site")
  );

  const sitesTagList =
    useSelector((state) => {
      return state.sitesReducer.siteTagList;
    }) || JSON.parse(sessionStorage.getItem("SitesTagList"));

  const tagListItem = useSelector((state) => state.sitesReducer.tagIdList);
  const [filteredRoiListArray, setFilteredRoiListArray] = useState([]);
  const [filteredTagsListArray, setFilteredTagsListArray] = useState([]);
  const [filteredTagsListArrayOfOA, setFilteredTagsListArrayOfOA] = useState([]);
  const [filteredTagsListArrayOfEE, setFilteredTagsListArrayOfEE] = useState([]);
  const [isSelectionFiltered, setIsSelectionFiltered] = useState(false);
  /* Location */
  const [accordionSiteDetails, setAccordionSiteDetails] = useState(
    persistedSelection?.accordionSiteDetails ??
      (state?.site !== undefined && Array.isArray(state.site) && state.site.length ? state.site[0]?.id : sites?.length ? sites[0]?.id : "All Site")
  );

  const [accordionSiteOpened, setAccordionSiteOpened] = useState(
    persistedSelection?.accordionSiteOpened ?? (state?.site !== undefined ? -1 : 0)
  );

  const [viewingCrossSelected, setViewingCrossSelected] = useState(
    selectedAreas.length > 0 || selectedFloors.length > 0 || selectedSubAreas.length > 0 || selectedSite.length > 0 ? true : false
  );
  const [siteTagid, setSiteTagId] = useState(accordionSiteDetails);
  const [floorTagid, setFloorTagId] = useState(selectedFloors.length > 0 ? selectedFloors[0].id : "");
  const [areaTagid, setAreaTagId] = useState();
  const [subAreaTagid, setSubAreaTagId] = useState();
  const dashboardSiteIndexApplied = useRef(false);

  const sitebasedFloor =
    siteReducer?.floors?.filter((f) => {
      return f?.tagids && Array.isArray(f.tagids) && f.tagids.length && f.tagids[0] === accordionSiteDetails;
    }) || [];
  const [matchingLocationFloors, setMatchingLocationFloors] = useState(sitebasedFloor);
  const [matchingLocationAreas, setMatchingLocationAreas] = useState([]);
  const [matchingLocationSubAreas, setMatchingLocationSubAreas] = useState([]);

  const resetAppSelction = () => {
    setSelectedSite([]);
    setSelectedFloors([]);
    setSelectedAreas([]);
    setSelectedSubAreas([]);
    setFilterList((prev) => ({
      ...prev,
      App: "All Apps"
    }));
    setAppName("All Apps");
    setEncryptPayload((prev) => ({
      ...prev,
      eventfield: undefined
    }))
    dispatch(clearStatisticsSelection());
  }

  useEffect(() => {
    if (!appNameFromQuery && !state) {
      resetAppSelction();
    }else{
      getAllBaselineCountData();
    }
  }, [appNameFromQuery])

  const getAllBaselineCountData = async () => {
    const result = await setAllTagsBaseLineCount(activeSession);
    if (result?.status === 200 || result?.status === 202) {
      dispatch(setAllBaselineCount(result?.data));
    } else {
      console.error("Failed to retrieve baseline data");
    }
  };

  useEffect(() => {
    if (appName || accordionSiteDetails) {
      let cvAppRoisWithCVAppsTemp = roiListItem?.filter((roiItem) => isValidRoiWithSiteAndApp(roiItem, allSiteIds, appName));
      let locationsWithRoiCvappsTemp = dataliter(cvAppRoisWithCVAppsTemp);
      const matchingListFloors =
        sites?.length && siteReducer?.floors?.length
          ? siteReducer.floors.filter((l) => {
              const floorAllAreas =
                siteReducer?.areas
                  ?.filter((f) => {
                    return f?.tagids !== undefined && Array.isArray(f.tagids) && f.tagids.length && f.tagids[0] === l.id;
                  })
                  .map((d) => d?.id) || [];

              const floorareas =
                siteReducer?.areas
                  ?.filter((f) => {
                    return (
                      f?.tagids !== undefined &&
                      Array.isArray(f.tagids) &&
                      f.tagids.length &&
                      f.tagids[0] === l.id &&
                      locationsWithRoiCvappsTemp.indexOf(f.id) !== -1
                    );
                  })
                  .map((d) => d?.id) || [];

              const floorAreasSubareas =
                siteReducer?.subareas
                  ?.filter((sa) => {
                    return (
                      sa?.tagids !== undefined &&
                      Array.isArray(sa.tagids) &&
                      sa.tagids.length &&
                      floorAllAreas.indexOf(sa.tagids[0]) > -1 &&
                      locationsWithRoiCvappsTemp.indexOf(sa.id) !== -1
                    );
                  })
                  .map((d) => d?.id) || [];

              if (
                l?.tagids !== undefined &&
                Array.isArray(l?.tagids) &&
                (locationsWithRoiCvappsTemp.indexOf(l.id) !== -1 || floorareas.length || floorAreasSubareas.length)
              ) {
                return l?.tagids.indexOf(accordionSiteDetails) !== -1;
              }
              return false;
            })
          : [];
      const matchingListAreas = siteReducer?.areas?.length
        ? siteReducer?.areas.filter((m) => {
            const areaSubAreas = siteReducer?.subareas
              ?.filter((f) => {
                return f?.tagids != undefined && f.tagids?.[0] == m?.id && locationsWithRoiCvappsTemp.indexOf(f?.id) > -1;
              })
              .map((d) => {
                return d?.id;
              });
            if (locationsWithRoiCvappsTemp.indexOf(m?.id) > -1 || areaSubAreas.length) {
              return true;
            }
          })
        : [];

      const matchingListSubAreas =
        siteReducer?.subareas?.length > 0
          ? siteReducer?.subareas.filter((m) => {
              if (locationsWithRoiCvappsTemp.indexOf(m.id) > -1) {
                if (
                  accordionSiteDetails ===
                  siteReducer?.floors?.find((e) => {
                    return (
                      e.id ===
                      siteReducer?.areas?.find((d) => {
                        return d?.id === m?.tagids?.[0];
                      })?.tagids?.[0]
                    );
                  })?.tagids?.[0]
                ) {
                  return true;
                }
              }
            })
          : [];
      setMatchingLocationFloors(matchingListFloors);
      setMatchingLocationAreas(matchingListAreas);
      setMatchingLocationSubAreas(matchingListSubAreas);
    }
    // roiListItem must be a dependency: it's fetched asynchronously (dispatch(getAllRoiListWithCvApps())
    // below) and is still empty on the very first render after a hard refresh, since polygonRoiReducer is
    // redux-persist blacklisted. Without this dependency, once appName/accordionSiteDetails are restored
    // immediately from a persisted selection snapshot, this effect fires once against an empty roiListItem
    // and never re-fires when the fetch resolves, leaving the Location tree (floors/areas/subareas) empty.
  }, [appName, accordionSiteDetails, sites, roiListItem]);

  const tagIdList = useSelector((state) => {
    return state.sitesReducer.tagIdList;
  });

  const getCameraName = (roi) => {
    var t = fovListItem.filter((m) => {
      return m?.id === roi?.parentdeviceid;
    })[0];
    var d = deviceListItem.cameraList.filter((k) => {
      return k?.id === t?.parentdeviceid;
    })[0];
    return d?.name;
  };

  const mapRoiListBasedOnSites = (_roiListItem, _appName) => {
    const foundCvApp = cvAppNames?.find((f) => {
      return f?.resources?.displayName === appNameForViewTag;
    });

    if (foundCvApp) {
      if (foundCvApp.category !== "Statistics Apps") {
        setShowObjectTypeDropdown(true);
      } else {
        setShowObjectTypeDropdown(false);
      }
    }

    var cvapp =
      cvAppNames?.filter((m) => {
        return m?.resources?.displayName === _appName.split("_")[0];
      }) || [];

    var toi = [];
    const filteredroiListItemwithCvvApps = _roiListItem?.filter((item) => item?.cvapps !== undefined && Array.isArray(item?.cvapps)) || [];

    cvapp.forEach((element) => {
      filteredroiListItemwithCvvApps?.forEach((m) => {
        m?.cvapps?.forEach((cvAppData) => {
          if (cvAppData?.image === element?.image) {
            toi.push(m);
          }
        });
      });
    });
    /*Add new prop to roi array of ParentSiteName to filter on basis of that 
    All site, floor, Area, Asubarea will appear under the parent sitename.. 
      */
    toi = toi.map((k) => {
      const sitename = sites?.find(f => f?.id === k?.siteids?.[0])?.name || "";

      if (sitename) {
        k.tagSiteName = sitename;
      } else {
        k.tagSiteName = "UnSubscribed";
      }
      return k;
    });

    /*To Goup by on basis of ParentSide  name   for the Roi List */
    var result = toi.reduce((x, y) => {
      (x[y.tagSiteName] = x[y.tagSiteName] || []).push(y);
      return x;
    }, {});
    return result;
  };

  const getFilteredRoiListArray = (result, fromFilter = false) => {
    let finalResult = [];
    Object.keys(result).forEach((m, mIndex) => {
      let roiList = [];

      result[m]?.forEach((roi, roiIndex) => {
        if (roiIndex > 0 && getCameraName(result[m][roiIndex]) === getCameraName(result[m][roiIndex - 1])) {
          roiList[roiList.length - 1].push(result[m][roiIndex]);
        } else {
          roiList.push([result[m][roiIndex]]);
        }
      });

      let siteName = Object.keys(result)[mIndex];
      let obj = { siteName, roiList };
      finalResult.push(obj);
    });
    setIsSelectionFiltered(fromFilter);
    setFilteredRoiListArray(finalResult);
  };

  useEffect(() => {
    if(siteReducer?.sites){
    var result = mapRoiListBasedOnSites(roiListItem, appName);
    let siteIds = arrangeAsAlphabeticalOrderWithField(
      siteReducer?.sites?.filter((f) => {
        return Object.keys(result).indexOf(f?.name) !== -1;
      }) || [],
      "name"
    );
    setSiteList(siteIds);
    let obj = {};
    if (siteIds.length > 0) {
      if (state === null) {
        if (selectedSite.length === 0) {
          setAccordionSiteDetails(siteIds[0]?.id);

          const roiTagObject = processUnmatchedTagMatches(
            mapRoiListBasedOnSites,
            roisListWithApps,
            appNameForViewTag,
            siteReducer
          );
          setFilterList((prevFilter) => ({
            ...prevFilter,
            Site: siteIds[0]?.name,
            Floor: roiTagObject.floors.map((item) => item.name),
            Area: roiTagObject.areas.map((item) => item.name),
            Subarea: roiTagObject.subareas.map((item) => item.name)
          }));
          setSelectedSite(roiTagObject.site);
          setSelectedFloors(roiTagObject.floors);
          setSelectedAreas(roiTagObject.areas);
          setSelectedSubAreas(roiTagObject.subareas);
          filterWidgetOnBasisOfAppSelected(appNameForViewTag, {
            site: roiTagObject.site,
            floors: roiTagObject.floors,
            areas: roiTagObject.areas,
            subareas: roiTagObject.subareas,
          });
          setEncryptPayload((prevPayload) => ({
            ...prevPayload,
            tagIdList: getTagListForEncryptAPI(roiTagObject.subareas, roiTagObject.areas, roiTagObject.floors, roiTagObject.site, siteList)?.join(","),
            eventfield: "fields." + getParentWidgetName(appName, cvAppNames) + ".resolutionTime",
            eventfieldForPriority: "fields." + getParentWidgetName(appName, cvAppNames) + ".severity"
          }));
          setSiteNameForViewTag(siteIds[0]?.name);
          //setSiteName(siteIds[0]?.id);
          setSelectedSite([siteIds[0]]);
          obj[siteIds[0]?.name] = result[siteIds[0]?.name];
          result = obj;
        } else {
          obj[selectedSite[0]?.name] = result[selectedSite[0]?.name];
          result = obj;
        }
      } else {
        setShowHideFilter(true);
        if (selectedSite.map((d) => d?.id).indexOf(state?.site?.[0]?.id) !== -1) {
          setAccordionSiteDetails(state?.site[0].id);
          setSiteNameForViewTag(state?.site.map((m) => m?.name).join(","));
          //setSiteName(state?.site[0].id);
          setFilterList((prevFilter) => ({
            ...prevFilter,
            Site: state?.site.map((m) => m?.name).join(",")
          }));
          obj[state?.site[0].name] = result[state?.site[0].name];
          result = obj;
          setSelectedSite(state?.site);
        } else {
          setAccordionSiteDetails(selectedSite[0]?.id);
          setSiteNameForViewTag(selectedSite.map((m) => m?.name).join(","));
          //setSiteName(selectedSite[0]?.id);
          setFilterList((prevFilter) => ({
            ...prevFilter,
            Site: selectedSite.map((m) => m?.name).join(",")
          }));
          obj[selectedSite[0]?.name] = result[selectedSite[0]?.name];
          result = obj;
          setSelectedSite(selectedSite);
        }
      }
    }

    if (Object.keys(result).length === 1 &&  Object.values(result)[0] !== undefined) {
      getFilteredRoiListArray(result);
    }
  }
    // roiListItem must be a dependency for the same reason as the matchingLocationFloors/Areas/SubAreas
    // effect above: siteList (which LocationSelection.jsx iterates to render the site accordion rows) is
    // derived from roiListItem via mapRoiListBasedOnSites, and roiListItem is empty on the very first
    // render after a hard refresh. Without this dependency, siteList never repopulates once the async
    // fetch resolves, so the whole Location accordion stays empty even though matchingLocationFloors/
    // Areas/SubAreas eventually compute correctly — there's simply no site row to expand.
  }, [appName, siteNameForViewTag, sites, roiListItem]);

  const buildUniqueTagsList = (tagItems) => {
    const selectedLocationList = getSelectedTagList(selectedSubAreas, selectedAreas, selectedFloors, selectedSite, siteReducer?.sites);
    const locationList = selectedLocationList.map(m => m.id);
    const uniqueTagsList = [];
    tagItems?.forEach((kObj) => {
      if (kObj?.siteids == undefined) {
        return;
      }
      kObj?.customdata?.statisticstagids?.forEach((k) => {
        if(locationList.includes(k)){
        uniqueTagsList.push({
          id: k,
          name: getWidgetLocationName(k, tagListItem)
        });
      }
      });
    });
    return uniqueTagsList;
  };

  useEffect(() => {
    if (filteredRoiListArray.length === 0) return;

    const appNameSelected = appNameForViewTag !== "Parking Occupancy" ? appNameForViewTag + "ing EE" : appNameForViewTag + " EE";
    const siteBasedRoiArray = isSelectionFiltered ? filteredRoiListArray :  filteredRoiListArray.filter(f => f.siteName === selectedSite?.[0]?.name);

    const filteredTagsListEE = [];
    const filteredTagsListOA = [];

    siteBasedRoiArray.forEach((item) => {
      if (!Array.isArray(item?.roiList)) return;
      item.roiList.forEach((k) => {
        if (!Array.isArray(k)) return;
        k.forEach((kObj) => {
          if (!Array.isArray(kObj?.cvapps)) return;
          kObj.cvapps.forEach((cvApp) => {
            if (cvApp?.name === appNameSelected) {
              filteredTagsListEE.push(kObj);
            } else {
              filteredTagsListOA.push(kObj);
            }
          });
        });
      });
    });

    const uniqueTagsListArray = buildUniqueTagsList(filteredTagsListEE);
    const uniqueTagsListArrayOA = buildUniqueTagsList(filteredTagsListOA);

    setFilteredTagsListArray(uniqueTagsListArray);
    setFilteredTagsListArrayOfEE([...new Set(uniqueTagsListArray.map(m => m.id))]);
    setFilteredTagsListArrayOfOA([...new Set(uniqueTagsListArrayOA.map(m => m.id))]);
  }, [filteredRoiListArray, appName]);

  // True between an App dropdown change and the next Apply click. While set, the roi/tag pipeline
  // (which re-runs on appName) must not write tagIdListEE/OA into encryptPayload, otherwise the
  // widgets refetch before Apply.
  const pendingAppChange = useRef(false);
  // Tag lists computed while an app change is pending; Apply merges them into its own payload write
  // so the widgets fetch once instead of once for Apply and again for the tag effects.
  const pendingTagPayload = useRef({});

  const applyEncryptPayloadTagUpdate = (filteredTagsListArrayOfSide, tagIdKey) => {
    if (!(filteredTagsListArrayOfSide.length > 0)) return;
    const locationTag = segregateLocationsOnBaisOfType(filteredTagsListArrayOfSide, siteReducer, selectedSite[0]);
    const tagIdList = getTagListForEncryptAPI(locationTag.subareas, locationTag.areas, locationTag.floors, locationTag.site, siteList)?.join(",");
    if (pendingAppChange.current) {
      pendingTagPayload.current[tagIdKey] = tagIdList;
      return;
    }
    setEncryptPayload((prevPayload) => ({
      ...prevPayload,
      [tagIdKey]: tagIdList
    }));
  };

  useEffect(() => {
    applyEncryptPayloadTagUpdate(filteredTagsListArrayOfEE, "tagIdListEE");
  }, [filteredTagsListArrayOfEE]);

  useEffect(() => {
    applyEncryptPayloadTagUpdate(filteredTagsListArrayOfOA, "tagIdListOA");
  }, [filteredTagsListArrayOfOA]);

  const [siteList, setSiteList] = useState([]);
  useEffect(() => {
    if (viewingCrossSelected) {
      setFilterList({
        ...filterlist,
        Floor: selectedFloors.map((item) => item.name),
        Area: selectedAreas.map((item) => item.name),
        Subarea: selectedSubAreas.map((item) => item.name)
      });
      filterWidgetOnBasisOfAppSelected(appName);
      setEncryptPayload({
        ...encryptPayload,
        tagIdList: getTagListForEncryptAPI(selectedSubAreas, selectedAreas, selectedFloors, selectedSite, siteList)?.join(",")
      });
      setViewingCrossSelected(false);
    }
  }, [viewingCrossSelected]);

  useEffect(() => {
    if (activeLocationAccordion !== 4 || dashboardSiteIndexApplied.current || !state?.site?.[0]?.id || !siteList.length) return;
    dashboardSiteIndexApplied.current = true;
    const targetId = selectedSite.length > 0 ? selectedSite[0]?.id : state.site[0]?.id;
    const idx = siteList.findIndex((f) => f?.id === targetId);
    if (idx !== -1) {
      setAccordionSiteOpened(idx);
    }
  }, [activeLocationAccordion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fallback: if floors loaded asynchronously after mount, re-derive the parent floor for area-level Dashboard redirects
  useEffect(() => {
    if (selectedFloors.length === 0 && state?.area?.length > 0 && siteReducer?.floors?.length > 0) {
      const parentFloor = siteReducer.floors.find((f) => f.id === state.area[0]?.tagids?.[0]);
      if (parentFloor) setSelectedFloors([parentFloor]);
    }
  }, [siteReducer?.floors]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedAccordionData({
      floors: selectedFloors,
      areas: selectedAreas,
      subareas: selectedSubAreas
    });
    if (state?.floor !== undefined || state?.area !== undefined || state?.subarea !== undefined) {
      setFilterList({
        ...filterlist,
        Floor: selectedFloors.map((item) => item.name),
        Area: selectedAreas.map((item) => item.name),
        Subarea: selectedSubAreas.map((item) => item.name)
      });
    }
  }, [selectedFloors, selectedAreas, selectedSubAreas]);

  //const { sites, loading, error } = useSelector((state) => state.sitesReducer);   "2024-09-18T13:00:50.000Z"
  // Only App accordion opened by default, rest closed
  
  const [activeAppNameAccordion, setActiveAppNameAccordion] = useState(2);
  const [activeObjectNameAccordion, setActiveObjectNameAccordion] = useState(null);
  const [activeDateAccordion, setActiveDateAccordion] = useState(null);
  const [activeAppearanceAccordion, setActiveAppearanceAccordion] = useState(null);

  const [isSingleSite, setIsSingleSite] = useState(false);

  const [startDateTime, setStartDateTime] = useState(getPickerDateFromUTCDate(new Date(Date.now()).setHours(0, 0, 0, 0)));

  const [endInputDate, setEndInputDate] = useState(new Date(Date.now()).toISOString());
  const [startInputDate, setStartInputDate] = useState(moment(new Date(Date.now()).setHours(0, 0, 0, 0)).toISOString());

  const [endDateTime, setEndDateTime] = useState(getPickerDateFromUTCDate(new Date(Date.now()).toISOString()));
  const [maxendDateTime, setMaxEndDateTime] = useState(getPickerDateFromUTCDate(new Date(Date.now()).toISOString()));

  const [encryptPayload, setEncryptPayload] = useState({
    accountname: activeSession?.accountId,
    tagid: sites?.length ? sites[0]?.id : "",
    startDate: getDateForKNOWIAPis(new Date(startInputDate)), //getUTCDateFromPickerDate(startDateTime), //"2024-11-05T10:00:00.000Z", //state?.tillDate ? state?.tillDate :new Date(Date.now() - 600 * 60 * 1000).toISOString(),
    histogram_fixedinterval: "1m",
    eeFixedInterval: "15m",
    endDate: new Date(Date.now()).toISOString(),
    extendedendDate: new Date(Date.now() - 1 * 60 * 1000).toISOString(),
    xAxisDateFormat: "hh:mmA",
    getTagsKind: sites?.length === 1 ? "ts.tag.floor" : "ts.tag.site",
    eventfield: "fields." + getParentWidgetName(appName, cvAppNames) + ".resolutionTime",
    eventfieldForPriority: "fields." + getParentWidgetName(appName, cvAppNames) + ".severity",
    objectClass: "",
    tagIdList: "id~" + siteTagid + "=name~" + (siteReducer?.sites?.find((f) => f?.id === siteTagid)?.name || ""),
    tagIdListEE: "",
    tagIdListOA: ""
  });
  //"edd142e5-068b-692a-e460-211f664aa1c6"
  const [filterlist, setFilterList] = useState(
    persistedSelection?.filterlist ?? {
      Site: siteReducer?.sites?.find((f) => f?.id === siteTagid)?.name || "",
      App: extractNameFromFullWidgetName(appName),
      Day: "Today"
    }
  );
  const [aggregatedCVAppCount, setAggregatedCVAppCount] = useState({});
  const [selectedFloorId, setSelectedFloorId] = useState("All Floor");
  const [selectedAreaId, setSelectedAreaId] = useState("All Area");
  const [startDateTimeForViewTag, setStartDateTimeForViewTag] = useState("Today");

  // Persist the current app/location selection so a hard browser refresh can restore it
  // (see the persistedSelection read logic near the top of this component). Skipped while
  // on the "All Apps" landing screen so nothing stale gets written before a real selection exists.
  useEffect(() => {
    if (!appName || appName === "All Apps") return;
    dispatch(
      setStatisticsSelection({
        appName,
        appNameForViewTag,
        selectedSite,
        selectedFloors,
        selectedAreas,
        selectedSubAreas,
        accordionSiteDetails,
        accordionSiteOpened,
        siteNameForViewTag,
        selectedSiteName,
        filterlist
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appName,
    appNameForViewTag,
    selectedSite,
    selectedFloors,
    selectedAreas,
    selectedSubAreas,
    accordionSiteDetails,
    accordionSiteOpened,
    siteNameForViewTag,
    selectedSiteName,
    filterlist
  ]);

  useEffect(() => {
     if (widgetsState.widgets.length === 0) {
      if (statisticsCharts?.chart === CHART_TYPES.KNOWI) {
        dispatch(fetchKnowiWidgets());
      } else {
      dispatch(storeWidgetList(complete_widgetList));
      }
    }
    dispatch(getAllRoiListWithCvApps());
  }, []);

  useEffect(() => {
    if (roiListWithCvappsLoadingStatus === ROI_LIST_WITH_CVAPPS_LOADING_STATUS.Loading) {
      setIsLoading(true);
    } else if (
      roiListWithCvappsLoadingStatus === ROI_LIST_WITH_CVAPPS_LOADING_STATUS.Loaded ||
      roiListWithCvappsLoadingStatus === ROI_LIST_WITH_CVAPPS_LOADING_STATUS.LoadingFailed
    ) {
      setIsLoading(false);
    }
  }, [roiListWithCvappsLoadingStatus]);

  useEffect(() => {
    if (selectedSite.length > 0) {
      filterWidgetOnBasisOfAppSelected(appName);
    }
    getObjectListAsPerSelectedCvApp(appName);
    getColorsListAsPerSelectedCvApp(appName);
    setIsSingleSite(siteList?.length === 1 ? true : false);
  }, [widgetsState.widgets]);

  useEffect(() => {}, [encryptPayload, eventAppObjectClassList, aggregatedCVAppCount]);

  //should implement error boundary here
  if (widgetsState.fetchErrorMessage) {
    return <div>{widgetsState.fetchErrorMessage}</div>;
    // return <Loader fullscreen={true} />;
  }

  function filterWidgetOnBasisOfAppSelected(widgetDisplayName, selectionOverride) {
    const effectiveSite = selectionOverride?.site ?? selectedSite;
    const effectiveFloors = selectionOverride?.floors ?? selectedFloors;
    const effectiveAreas = selectionOverride?.areas ?? selectedAreas;
    const effectiveSubAreas = selectionOverride?.subareas ?? selectedSubAreas;

    var taggedSelection =
      effectiveSubAreas.length > 0
        ? effectiveSubAreas
        : effectiveAreas.length > 0
          ? effectiveAreas
          : effectiveFloors.length > 0
            ? effectiveFloors
            : effectiveSite.length > 0
              ? effectiveSite
              : sites.filter((f) => {
                  return effectiveSite.map((m) => m?.id).indexOf(f?.id) != -1;
                });
    const j = [
      ...new Set(
        filterRoiCvappfromSiteSelection(filteredCvAppROiList, widgetDisplayName, taggedSelection).map((m) => {
          return m.cvapps[0].image;
        })
      )
    ];
    var EEVisibility = j.filter((f) => {
      return f?.indexOf("EE") > -1;
    });
    setBaselineVisibility(EEVisibility);

    var d = [];
    let baseWidgetName = "";
    if (widgetDisplayName != "All Apps") {
      baseWidgetName = extractNameFromFullWidgetNameForWidgetFilterOFFLoors(widgetDisplayName, widgetListFromWidgetstate);
      d = widgetListFromWidgetstate.filter((m) => {
        return m.name.indexOf(baseWidgetName + "loc_") > -1;
      });
      if (j[0] != "ParkingBayOccupancy") {
        if (j.length === 1 && (j[0].indexOf("OA") > -1 || j[0].indexOf("EE") > -1)) {
          const fCvappJ = cvAppNames.find((f) => {
            return f.image === j[0];
          });
          if (fCvappJ.category === "Statistics Apps") {
            const chartby = fCvappJ.image.substring(fCvappJ.image.length - 2, fCvappJ.image.length).toLowerCase();
            const filteredByChartType = d.filter((l) => {
              return l.name.indexOf("_" + chartby + "_") > -1;
            });
            if (filteredByChartType.length > 0) {
              d = filteredByChartType;
            }
          }
        }
      } else {
        if (j.length === 1) {
          const fCvappJ = cvAppNames.find((f) => {
            return f.image === j[0];
          });
          if (fCvappJ.category === "Statistics Apps") {
            const appImageTemp = fCvappJ.image.replaceAll(" ", "").toLowerCase();
            const chartby = j[0] == "ParkingBayOccupancy" ? "fa" : appImageTemp.substring(appImageTemp.length - 2, appImageTemp.length).toLowerCase();
            const filteredByChartType = d.filter((l) => {
              return l.name.indexOf("_" + chartby + "_") > -1;
            });
            if (filteredByChartType.length > 0) {
              d = filteredByChartType;
            }
          }
        }
      }

      d = d?.map((k) => {
        if (k?.name.indexOf("_ev_eventnameloc_1122") > -1) {
          k.name = extractNameFromFullWidgetName(widgetDisplayName) + "_ev_eventnameloc_1122";
        }
        return k;
      });

      const orderedWidgetList = d.sort(function (a, b) {
        return a?.name.split("_")[a?.name.split("_").length - 2] - b?.name.split("_")[b?.name.split("_").length - 2];
      });

      const g =
        getSelectedTagList(effectiveSubAreas, effectiveAreas, effectiveFloors, effectiveSite, siteList).length === 1
          ? orderedWidgetList.filter((f) => {
              return f.name.indexOf("_xhm_") === -1;
            })
          : orderedWidgetList;


      setWidgetList(g);
    }
  }

  function getObjectListAsPerSelectedCvApp(appname) {
    const roilistAppname = roiListItem?.filter((roiItem) => isValidRoiWithSiteAndApp(roiItem, [], appName));
    var obj = cvAppNames?.find((f) => f?.resources?.displayName === appname.split("_")[0]);
    var objStatParam = obj?.configtemplate?.schema?.properties?.["deploy-parameters"];
    var listName = objStatParam?.properties?.objects?.items?.enum ?? [];

    if (listName.length) {
      var objListfromRoi =
        roilistAppname
          ?.map((m) => {
            return m?.deployments
              ?.map((d) => {
                return Object.keys(d?.config?.["deploy-parameters"] || {}).length === 0 ? listName : d?.config?.["deploy-parameters"]?.objects;
              })
              .flat();
          })
          .flat() || [];

      listName = arrangeAsAlphabeticalOrder(objListfromRoi.filter((element) => element !== undefined));
      listName = [...new Set(listName)];
    }
    setEventAppObjectClassList(listName);
  }

  function getColorsListAsPerSelectedCvApp(appname) {
    const roilistAppname = roiListItem?.filter((roiItem) => isValidRoiWithSiteAndApp(roiItem, [], appName));
    var obj = cvAppNames?.find((f) => f?.resources?.displayName === appname.split("_")[0]);
    var objStatParam = obj?.configtemplate?.schema?.properties?.["deploy-parameters"];
    var listName = objStatParam?.properties?.colors?.items?.enum ?? [];

    if (listName.length) {
      var objListfromRoi =
        roilistAppname
          ?.map((m) => {
            return m?.deployments
              ?.map((d) => {
                return d?.config?.["deploy-parameters"]?.colors;
              })
              .flat();
          })
          .flat() || [];

      listName = arrangeAsAlphabeticalOrder(objListfromRoi);
      listName = [...new Set(listName)];
    }

    listName = listName.filter(function (element) {
      return element !== undefined;
    });

    setEventAppColorsClassList(listName);
    setSelectUpperClothingColors(listName);
    setSelectLowerClothingColors(listName);
  }
  function setFilterType(e) {
    const roilistAppname = roiListItem?.filter((roiItem) => isValidRoiWithSiteAndApp(roiItem, [], e.currentTarget.ariaLabel));
    const g = setLocationsWithRoiCvapps(dataliter(roilistAppname));
    const [selectedApp] = e.currentTarget.ariaLabel.split("_") || [""];

    var obj = WidgetListFromCvApp?.find((f) => f.name === e.currentTarget.ariaLabel);

    if (state === undefined && sites.length >= 1) {
      setSelectedSiteAppName(sites[0].name);
      // setSiteName(sites[0].id);
    }

    setSelectedFloorId("All Floor");
    setSelectedAreaId("All Area");

    getObjectListAsPerSelectedCvApp(e.currentTarget.ariaLabel);
    getColorsListAsPerSelectedCvApp(e.currentTarget.ariaLabel);

    setFilterList({
      Site: sites?.find((f) => f.id === siteTagid)?.name,
      App: selectedApp, //e.target.options[e.target.selectedIndex].text,
      Day: startDateTimeForViewTag
    });
    setAppName(selectedApp);
    setAppNameForViewTag(extractNameFromFullWidgetName(e.currentTarget.ariaLabel));
    filterWidgetOnBasisOfAppSelected(e.currentTarget.ariaLabel, selectedSiteName);
    navigate(`/statistics?appName=${setpathParam(selectedApp)}`);
  }

  const reloadWidgetsAfterForceReset = () => {
    setEncryptPayload({ ...encryptPayload, getTagsKind: "ts.kind.area" });
    setBaselineDataSourcesToggleDropdown(!baselineDataSourcesToggleDropdown);
  };

  const statisticsContextValue = {
    // State
    appName,
    activeSession,
    setAppName,
    appNameForViewTag,
    setAppNameForViewTag,
    baselineVisibility,
    setBaselineVisibility,
    showHideFilter,
    setShowHideFilter,
    widgetList,
    setWidgetList,
    cvAppNames,
    roiListItem,
    roisListWithApps,
    filteredCvAppROiList,
    floors,
    setFloorsTagList,
    areaTagList,
    setAreasTagList,
    subAreaTagList,
    setSubAreasTagList,
    eventAppObjectClassList,
    setEventAppObjectClassList,
    eventAppColorsClassList,
    setEventAppColorsClassList,
    selectUpperClothingColors,
    setSelectUpperClothingColors,
    selectLowerClothingColors,
    setSelectLowerClothingColors,
    showObjectTypeDropdown,
    setShowObjectTypeDropdown,
    selectedSite,
    setSelectedSite,
    selectedFloors,
    setSelectedFloors,
    selectedAreas,
    setSelectedAreas,
    selectedSubAreas,
    setSelectedSubAreas,
    selectAccordionData,
    setSelectedAccordionData,
    selectedEventAppObject,
    setSelectedEventAppObject,
    activeDatepickerListIndex,
    setActiveDatepickerListIndex,
    siteNameForViewTag,
    setSiteNameForViewTag,
    selectedSiteName,
    setSelectedSiteAppName,
    sitesTagList,
    tagListItem,
    filteredRoiListArray,
    setFilteredRoiListArray,
    filteredTagsListArray,
    setFilteredTagsListArray,
    accordionSiteDetails,
    setAccordionSiteDetails,
    accordionSiteOpened,
    setAccordionSiteOpened,
    viewingCrossSelected,
    pendingAppChange,
    pendingTagPayload,
    setViewingCrossSelected,
    siteTagid,
    setSiteTagId,
    floorTagid,
    setFloorTagId,
    areaTagid,
    setAreaTagId,
    subAreaTagid,
    setSubAreaTagId,
    matchingLocationFloors,
    setMatchingLocationFloors,
    matchingLocationAreas,
    setMatchingLocationAreas,
    matchingLocationSubAreas,
    setMatchingLocationSubAreas,
    siteList,
    setSiteList,
    startDateTime,
    setStartDateTime,
    endInputDate,
    setEndInputDate,
    startInputDate,
    setStartInputDate,
    endDateTime,
    setEndDateTime,
    maxendDateTime,
    setMaxEndDateTime,
    encryptPayload,
    setEncryptPayload,
    filterlist,
    setFilterList,
    aggregatedCVAppCount,
    setAggregatedCVAppCount,
    selectedFloorId,
    setSelectedFloorId,
    selectedAreaId,
    setSelectedAreaId,
    startDateTimeForViewTag,
    setStartDateTimeForViewTag,
    baselineDataSourcesToggleDropdown,
    setBaselineDataSourcesToggleDropdown,
    appNameToggleDropdown,
    setAppNameToggleDropdown,
    objectNameToggleDropdown,
    setObjectNameToggleDropdown,
    siteNameToggleDropdown,
    setSiteNameToggleDropdown,
    activeLocationAccordion,
    setActiveLocationAccordion,
    activeAppNameAccordion,
    setActiveAppNameAccordion,
    activeObjectNameAccordion,
    setActiveObjectNameAccordion,
    activeDateAccordion,
    setActiveDateAccordion,
    activeAppearanceAccordion,
    setActiveAppearanceAccordion,
    isSingleSite,
    setIsSingleSite,
    isLoading,
    setIsLoading,
    setAllBaselineCount,
    setAllTagsBaseLineCount,
    cvappNameList,
    landingCVAppNameList,
    tagIdList,
    state,
    setLocationsWithRoiCvapps,
    sites,
    siteReducer,

    // Methods (add only those needed by children)
    dataliter,
    filterWidgetOnBasisOfAppSelected,
    getFilteredRoiListArray,
    mapRoiListBasedOnSites,
    reloadWidgetsAfterForceReset,
    setFilterType,
    getSelectedTagList,
    getObjectListAsPerSelectedCvApp,
    getColorsListAsPerSelectedCvApp,
    getTagListForEncryptAPI,
    mapRoiListBasedOnSites,
  };

  return (
    <StatisticProvider value={statisticsContextValue}>
      <div>
        {widgetsState?.loading ? (
          <div className="statistics-loader-wrapper">
            <Loader fullscreen={false} active={true} surface="light" />
          </div>
        ) : (
          <>
            <GettingStartedModal />
            {filterlist.App !== "All Apps" ? (
              <Breadcrumbs surface="light">
                <BreadcrumbItem>
                  <Link data-testid="statistics_landing_page" onClick={resetAppSelction} to="/statistics" className="breadcrums-item_link">
                    Statistics
                  </Link>
                </BreadcrumbItem>
                <BreadcrumbItem>{filterlist.App}</BreadcrumbItem>
              </Breadcrumbs>
            ) : (
              <></>
            )}
            <h2 className="breadcrumb-css">Statistics</h2>

            {!appNameFromQuery ? <LandingFilterBar /> : <StatsApp />}

            <br />
            <br />

            
          </>
        )}
      </div>
    </StatisticProvider>
  );
};

export default Statistics;
